<template>
  <div class="p-4 flex gap-2">
    <div>
      <Select v-model="statusValue" @update:modelValue="onUpdateStatus($event as ModelOrderResponseStateEnum | 'ALL')">
        <SelectTrigger>
          <SelectValue :value="statusValue" class="font-medium" placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Order Status</SelectLabel>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="PARTIAL">Partial</SelectItem>
            <SelectItem value="PLACED">Placed</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
            <SelectItem value="SUSPENDED">Suspended</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="CANCELLING">Cancelling</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Select v-model="dateTypeValue" @update:model-value="onUpdateDateType">
        <SelectTrigger>
          <SelectValue :value="dateTypeValue" class="font-medium" placeholder="Date Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Date Type</SelectLabel>
            <SelectItem value="updatedAt">Updated</SelectItem>
            <SelectItem value="createdAt">Created</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Popover>
        <PopoverTrigger as-child>
          <Button
            variant="outline"
            :class="cn('w-[280px] justify-start text-left font-normal', !startEndDates && 'text-muted-foreground')"
          >
            <CalendarIcon class="mr-2 h-4 w-4" />
            <template v-if="startEndDates.start">
              <template v-if="startEndDates.end">
                <span class="font-medium">
                  {{ df.format(startEndDates.start.toDate(getLocalTimeZone())) }} -
                  {{ df.format(startEndDates.end.toDate(getLocalTimeZone())) }}
                </span>
              </template>

              <template v-else>
                <span class="font-medium"></span>
                {{ df.format(startEndDates.start.toDate(getLocalTimeZone())) }}
              </template>
            </template>
            <template v-else>
              <span class="font-medium">Select Dates</span>
            </template>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0">
          <RangeCalendar
            v-model="startEndDates"
            initial-focus
            :number-of-months="2"
            @update:modelValue="onDateChange"
          />
        </PopoverContent>
      </Popover>
    </div>
    <div>
      <Button variant="outline" type="button" @click="onClearFilters">
        <CircleX class="h-4 w-4" />
        Clear
      </Button>
    </div>
    <div class="w-full max-w-[350px] ml-auto">
      <div class="relative">
        <Search class="h-4 w-4 font-bold text-grey-light-500 absolute left-2 top-1/2 transform -translate-y-1/2" />
        <Input v-model="searchTerm" placeholder="Search" class="pl-8" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RangeCalendar } from '@/components/ui/range-calendar';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { debounce } from '@/utils/lowDash';
import { DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { Calendar as CalendarIcon, CircleX, Search } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { onMounted, ref, watch, type Ref } from 'vue';
import type { ModelOrderResponseStateEnum } from '@layup/api-sdk';

const emit = defineEmits([
  'update:status',
  'update:dateType',
  'update:dateRange',
  'update:clearFilters',
  'update:searchTerm',
]);

const status = ref<ModelOrderResponseStateEnum | ''>('');
const statusValue = ref<string | undefined>('PLACED');
const dateTypeValue = ref<string | undefined>(undefined);
const searchTerm = ref('');
const startEndDates = ref({ start: undefined, end: undefined }) as Ref<DateRange>;

onMounted(async () => {
  watch(startEndDates, (newValue) => {
    onDateChange(newValue);
  });

  watch(searchTerm, (newValue) => {
    onSearch(newValue);
  });
});

const df = new DateFormatter('en-US', {
  dateStyle: 'medium',
});

const onUpdateStatus = (value: ModelOrderResponseStateEnum | 'ALL') => {
  value === 'ALL' ? (status.value = '') : (status.value = value);
  emit('update:status', value === 'ALL' ? '' : value);
};

const onUpdateDateType = (dateType: string) => {
  emit('update:dateType', dateType);
};

const onDateChange = (newValue: DateRange) => {
  if (newValue.start && newValue.end) {
    startEndDates.value = newValue;
    emit('update:dateRange', startEndDates.value);
  }
};

const onClearFilters = () => {
  status.value = '';
  statusValue.value = undefined;
  dateTypeValue.value = undefined;
  searchTerm.value = '';
  startEndDates.value = { start: undefined, end: undefined };
  emit('update:clearFilters');
};

const onSearch = debounce((search: string) => {
  emit('update:searchTerm', search);
}, 300);
</script>
